import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import { useSurveyParams } from '../context/SurveyParamsContext'
import {
  convertQuestionMappings,
  convertVisibleIf,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const userIdRef = useRef(null)
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  // 上一頁預設不開（必須是 null，不可省略）
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 跳題 小心複選題要用陣列（題號 1-based，工具自動 -1）
  const visibleIfConditionsInit = convertVisibleIf({
    // 3-1-1 父親國籍：選項2=(1-2)新住民 → 3-1-1-1-2；選項4=(2)外國籍 → 3-1-1-2；其餘直接到 3-1-2
    q13: '{q12} = 2',
    q14: '{q12} = 4',
    // 3-1-2 母親國籍（同上鏡像）；其餘直接到 3-2
    q16: '{q15} = 2',
    q17: '{q15} = 4',
    // 4-1 身心障礙證明選(2)無 → 跳過 4-2，直接到 5-1
    q24: '{q23} = 1',
    // 5-3 排序題有排入(15)其他 → 顯示 5-3-15 說明（排序題其他為 o_type 0，用數字索引）
    q28: '{q27} anyof [15]',
    // 5-5 選(1)沒有運動 → 跳過 5-5-2-1~5-5-2-4，直接到 5-6-1
    q32: '{q31} = 2',
    q33: '{q31} = 2',
    q34: '{q31} = 2',
    q35: '{q31} = 2',
    // 5-6-1 選(1)完全沒有上網 → 跳過 5-6-1-2~5-6-5-2，直接到 5-7-1
    q37: '{q36} = 2',
    q38: '{q36} = 2',
    q39: '{q36} = 2',
    q40: '{q36} = 2',
    q41: '{q36} = 2',
    q42: '{q36} = 2',
    q43: '{q36} = 2',
    // 2-1 父親(1)或母親(2)至少勾一個才顯示 5-8、5-9（兩者都沒勾 → 隱藏）
    q46: '{q5} anyof [1, 2]',
    q47: '{q5} anyof [1, 2]',
    // 5-14 選(1)完全沒有 → 跳過 5-15、5-16，直接到 5-17
    q55: '({q54} notempty) and ({q54} <> 1)',
    q56: '({q54} notempty) and ({q54} <> 1)',
    // 5-20 選(1)從來沒有打工 → 跳過 5-20-2-1~5-20-2-6，直接到 5-21
    q61: '{q60} = 2',
    q62: '{q60} = 2',
    q63: '{q60} = 2',
    q64: '{q60} = 2',
    q65: '{q60} = 2',
    q66: '{q60} = 2',
    // 5-21 選(1)沒有 → 跳過 5-21-2、5-22，直接到 5-23
    q68: '{q67} = 2',
    q69: '{q67} = 2',
    // 6-3-1 ~ 6-3-14：選項3=(B1)知道但從未用過 → 顯示 B1 題；選項4=(B2)知道且曾使用過 → 顯示 B2 題
    q75: '{q74} = 3',
    q76: '{q74} = 4',
    q78: '{q77} = 3',
    q79: '{q77} = 4',
    q81: '{q80} = 3',
    q82: '{q80} = 4',
    q84: '{q83} = 3',
    q85: '{q83} = 4',
    q87: '{q86} = 3',
    q88: '{q86} = 4',
    q90: '{q89} = 3',
    q91: '{q89} = 4',
    q93: '{q92} = 3',
    q94: '{q92} = 4',
    q96: '{q95} = 3',
    q97: '{q95} = 4',
    q99: '{q98} = 3',
    q100: '{q98} = 4',
    q102: '{q101} = 3',
    q103: '{q101} = 4',
    q105: '{q104} = 3',
    q106: '{q104} = 4',
    q108: '{q107} = 3',
    q109: '{q107} = 4',
    q111: '{q110} = 3',
    q112: '{q110} = 4',
    q114: '{q113} = 3',
    q115: '{q113} = 4',
    // 6-3-15 其他服務(q116)未填 → 跳過 6-3-15-B/B1/B2，直接到 6-4-1；B 題選項3=B1、選項4=B2
    q117: '{q116} notempty',
    q118: '({q116} notempty) and ({q117} = 3)',
    q119: '({q116} notempty) and ({q117} = 4)',
    // 6-4-1 選(1)未曾參與 → 跳過 6-4-2，直接到 6-5
    q121: '({q120} notempty) and ({q120} <> 1)',
  })

  // 非必填(從 0 計算)
  // 6-3-15（題號116）其他服務開放填答非必填，未填才能跳到 6-4-1；notRequired 無轉換工具，吃 0-based 原始索引
  const notRequired = [115]

  const mappedRateChoice = null
  const pageBreakQuestions = null

  // 篩出/結束條件
  const triggersConditionsInit = null

  // 帶出: 帶入 (用1開始計算填這裡)
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    sfdSamplerId,
    sfdPId,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    sfdSamplerId,
    webAuthType,
  })

  useEffect(() => {
    if (surveyModel) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [surveyModel])

  // 當 surveyModel 建立時，自動捲動到頂端
  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {
    // 參考 P231113152849 或 P231101165555
  }

  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>感謝您的填答～歡迎轉發分享！<br><br>想參加更多問卷調查活動？手指點點就可以抽獎賺好康！<br>點擊下方按鈕加入 EZChoice APP<br>或複製下載連結 <a href="https://ezc-appweb.dsigroup.com.tw/app.html" target="_blank">https://ezc-appweb.dsigroup.com.tw/app.html</a><br>裡面還有更多填問卷拿購物金的活動唷～<br>各種好玩的問卷等你來玩！<br><br>想知道更多好康問卷、活動消息，請追蹤關注<br><a href="https://www.facebook.com/EasyCTRs" target="_blank">【輕鬆點點】粉絲專頁</a><br><br>加入我們的 <a href="https://lin.ee/HWETcqy" target="_blank">LINE@</a>，<br>隨時得到最新填問卷獲好禮的消息。<br><br><h4 style="text-align: center">請按「完成」前往下載 EZChoice 或填寫更多問卷</h4></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    userIdRef,
    enableProgressSaving: webAuthType > 0 || !!sfdSamplerId,
    sfdSamplerId,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
